import { useRef, useEffect, useState, useCallback } from 'react';
import * as d3 from 'd3';
import { crmService } from '../../services/crmService';
import { getOrgLogo } from '../../utils/organizationLogos';

// Visual encoding for relationship types
const LINK_STYLES = {
  reports_to:           { stroke: '#E5E7EB', width: 2.5, dash: null,      label: 'Chain of Command' },
  supervises:           { stroke: '#E5E7EB', width: 2.5, dash: null,      label: 'Chain of Command' },
  peer:                 { stroke: '#4B5563', width: 1,   dash: null,      label: 'Peer' },
  linkedin:             { stroke: '#0A66C2', width: 1.5, dash: '6,4',     label: 'LinkedIn' },
  working_group:        { stroke: '#3B82F6', width: 2,   dash: '10,4',    label: 'Working Group' },
  program_team:         { stroke: '#8B5CF6', width: 2,   dash: '10,4',    label: 'Program Team' },
  mentor:               { stroke: '#10B981', width: 1.5, dash: '5,3',     label: 'Mentor/Mentee' },
  mentee:               { stroke: '#10B981', width: 1.5, dash: '5,3',     label: 'Mentor/Mentee' },
  former_colleague:     { stroke: '#6B7280', width: 1,   dash: '3,3',     label: 'Former Colleague' },
  congressional_liaison:{ stroke: '#EF4444', width: 2,   dash: '8,4',     label: 'Congressional' },
  contractor_support:   { stroke: '#F59E0B', width: 1.5, dash: '5,5',     label: 'Contractor Support' },
  competitor_contact:   { stroke: '#DC2626', width: 1.5, dash: '4,4',     label: 'Competitor' },
  partner_contact:      { stroke: '#06B6D4', width: 1.5, dash: '7,3',     label: 'Partner' },
  advisory:             { stroke: '#A78BFA', width: 1.5, dash: '7,3',     label: 'Advisory' },
  org_member:           { stroke: '#1F2937', width: 0.5, dash: null,      label: 'Org Member' },
  market:               { stroke: '#DC2626', width: 1.5, dash: null,      label: 'Shared PSC' },
  branchlink:           { stroke: '#334155', width: 1,   dash: null,      label: 'Branch' },
};

const NODE_COLORS = {
  commander:            '#DC2626',
  decision_maker:       '#EA580C',
  process_owner:        '#D97706',
  elected_official:     '#7C3AED',
  political_appointee:  '#6D28D9',
  congressional_staff:  '#9333EA',
  end_user:             '#16A34A',
  staff:                '#2563EB',
  contractor_support:   '#6B7280',
  researcher:           '#0891B2',
  advisor:              '#0D9488',
  liaison:              '#0D9488',
  organization:         '#374151',
};

const nodeRadius = (d) => {
  if (d.market === 'branch') return 34;
  if (d.nodeType === 'organization') return Math.max(12, Math.min(30, (d.contactCount || 1) * 3));
  return Math.max(7, Math.min(22, (d.authorityScore || 30) / 4.5));
};

const nodeColor = (d) => {
  if (d.market === 'branch') return '#F59E0B';   // branch anchor — gold
  if (d.market === 'office') return '#2563EB';   // government office — blue
  if (d.market === 'company') return '#DC2626';  // competitor company — red
  if (d.nodeType === 'organization') return NODE_COLORS.organization;
  return NODE_COLORS[d.positionType] || '#4B5563';
};

const ALL_REL_TYPES = Object.keys(LINK_STYLES).filter(k => !['org_member', 'market', 'branchlink'].includes(k));

export default function NetworkGraph({ onNodeClick }) {
  const svgRef = useRef(null);
  const containerRef = useRef(null);
  const simulationRef = useRef(null);
  const zoomRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [graphData, setGraphData] = useState({ nodes: [], links: [] });
  const [selectedNode, setSelectedNode] = useState(null);
  const [tooltip, setTooltip] = useState(null);

  // Filters
  const [showOrgMembers, setShowOrgMembers] = useState(true);
  const [activeRelTypes, setActiveRelTypes] = useState(
    new Set(['reports_to','supervises','linkedin','working_group','program_team','congressional_liaison'])
  );
  const [minAuthority, setMinAuthority] = useState(0);
  const [orgFilter, setOrgFilter] = useState('');
  const [orgList, setOrgList] = useState([]);
  const [viewMode, setViewMode] = useState('contacts'); // 'contacts' | 'market'
  const [marketTier, setMarketTier] = useState('');     // '' | platform | component | service | other
  const [marketDept, setMarketDept] = useState('');     // department label filter
  const [marketMeta, setMarketMeta] = useState({});     // psc code → {family,title,tier}
  const [marketDepts, setMarketDepts] = useState([]);   // department options
  const [searchTerm, setSearchTerm] = useState('');
  const [highlightIds, setHighlightIds] = useState(new Set());

  const loadGraph = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      if (viewMode === 'market') {
        // High-level market map: government offices (blue) ↔ competitor companies (red), linked by
        // shared PSC. Logos only, no contacts. Reuses the org-node rendering (logos) via nodeType.
        const m = await crmService.getMarket({ tier: marketTier || undefined, dept: marketDept || undefined });
        setMarketMeta(m.pscMeta || {});
        setMarketDepts(m.departments || []);
        const offices = (m.offices || []).map(o => ({ id: o.id, nodeType: 'organization', label: o.label, fullName: o.fullName || o.label, logo_url: o.logo_url, contactCount: o.pscCount || 1, market: 'office', department: o.department || 'Other' }));
        const companies = (m.companies || []).map(c => ({ id: c.id, nodeType: 'organization', label: c.label, fullName: c.label, logo_url: c.logo_url, contactCount: c.pscCount || 1, market: 'company' }));
        // Structural backbone: a pinned anchor per department present, offices linked to their department.
        const depts = [...new Set(offices.map(o => o.department))];
        const deptNodes = depts.map(d => ({ id: `dept_${d}`, nodeType: 'organization', label: d, fullName: d, market: 'branch', contactCount: 8 }));
        const deptLinks = offices.map(o => ({ id: `dl_${o.id}`, source: `dept_${o.department}`, target: o.id, type: 'branchlink', strength: 1 }));
        const nodes = [...deptNodes, ...offices, ...companies];
        const links = [...deptLinks, ...(m.edges || []).map(e => ({ id: `${e.office_id}__${e.company_id}`, source: e.office_id, target: e.company_id, type: 'market', strength: e.weight || 1, shared: e.shared }))];
        setGraphData({ nodes, links });
        return;
      }
      const sanitizedMinAuthority = parseInt(minAuthority) || 0;
      const data = await crmService.getNetwork({
        min_authority: sanitizedMinAuthority > 0 ? sanitizedMinAuthority : undefined,
        rel_types: [...activeRelTypes].join(','),
        org_id: orgFilter || undefined,
      });
      setGraphData(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [minAuthority, activeRelTypes, orgFilter, viewMode, marketTier, marketDept]);

  useEffect(() => { loadGraph(); }, [loadGraph]);

  // Load the org list once for the company/organization selector (only orgs that have contacts).
  useEffect(() => {
    crmService.getOrganizations()
      .then(d => setOrgList((Array.isArray(d) ? d : (d.organizations || d.rows || [])).filter(o => Number(o.contact_count) > 0)))
      .catch(() => {});
  }, []);

  // Search highlight
  useEffect(() => {
    if (!searchTerm.trim()) { setHighlightIds(new Set()); return; }
    const term = searchTerm.toLowerCase();
    const ids = new Set(
      graphData.nodes
        .filter(n =>
          n.label?.toLowerCase().includes(term) ||
          n.fullName?.toLowerCase().includes(term) ||
          n.orgName?.toLowerCase().includes(term) ||
          n.title?.toLowerCase().includes(term)
        )
        .map(n => n.id)
    );
    setHighlightIds(ids);
  }, [searchTerm, graphData.nodes]);

  // D3 rendering
  useEffect(() => {
    if (!svgRef.current || loading || graphData.nodes.length === 0) return;

    const container = containerRef.current;
    if (!container) return;

    const width = container.clientWidth || 900;
    const height = container.clientHeight || 600;

    const svg = d3.select(svgRef.current)
      .attr('width', width)
      .attr('height', height);

    svg.selectAll('*').remove();

    // Defs for arrowheads
    const defs = svg.append('defs');
    Object.entries(LINK_STYLES).forEach(([type, style]) => {
      defs.append('marker')
        .attr('id', `arrow-${type}`)
        .attr('viewBox', '0 -4 8 8')
        .attr('refX', 14)
        .attr('refY', 0)
        .attr('markerWidth', 6)
        .attr('markerHeight', 6)
        .attr('orient', 'auto')
        .append('path')
        .attr('d', 'M0,-4L8,0L0,4')
        .attr('fill', style.stroke)
        .attr('opacity', 0.7);
    });

    const g = svg.append('g');

    // Zoom
    const zoom = d3.zoom()
      .scaleExtent([0.1, 4])
      .on('zoom', (event) => {
        g.attr('transform', event.transform);
      });
    zoomRef.current = zoom;
    svg.call(zoom);

    // Filter links
    const visibleLinks = graphData.links.filter(l => {
      if (l.type === 'market' || l.type === 'branchlink') return true;
      if (l.type === 'org_member') return showOrgMembers;
      return activeRelTypes.has(l.type);
    });

    // Filter nodes to only those connected or orgs
    const connectedIds = new Set();
    visibleLinks.forEach(l => {
      connectedIds.add(typeof l.source === 'object' ? l.source.id : l.source);
      connectedIds.add(typeof l.target === 'object' ? l.target.id : l.target);
    });
    const visibleNodes = graphData.nodes.filter(n =>
      n.nodeType === 'organization' || connectedIds.has(n.id) ||
      graphData.links.length === 0
    );

    // Simulation
    const simulation = d3.forceSimulation(visibleNodes)
      .force('link', d3.forceLink(visibleLinks)
        .id(d => d.id)
        .distance(d => {
          if (d.type === 'org_member') return 80;
          if (d.type === 'reports_to' || d.type === 'supervises') return 70;
          return 120;
        })
        .strength(d => d.type === 'org_member' ? 0.1 : 0.4)
      )
      .force('charge', d3.forceManyBody().strength(-250))
      .force('center', d3.forceCenter(width / 2, height / 2))
      .force('collide', d3.forceCollide(d => nodeRadius(d) + 8));

    simulationRef.current = simulation;

    // Pin branch anchors (market map) so offices cluster around their branch — a stable backbone.
    const branchAnchors = visibleNodes.filter(n => n.market === 'branch');
    branchAnchors.forEach((n, i) => {
      if (branchAnchors.length <= 1) { n.fx = width / 2; n.fy = height / 2; }
      else {
        const a = (i / branchAnchors.length) * 2 * Math.PI - Math.PI / 2;
        const R = Math.min(width, height) * 0.2;
        n.fx = width / 2 + Math.cos(a) * R;
        n.fy = height / 2 + Math.sin(a) * R;
      }
    });

    // Draw links
    const link = g.append('g').attr('class', 'links')
      .selectAll('line')
      .data(visibleLinks)
      .join('line')
      .attr('stroke', d => LINK_STYLES[d.type]?.stroke || '#555')
      .attr('stroke-width', d => LINK_STYLES[d.type]?.width || 1)
      .attr('stroke-dasharray', d => LINK_STYLES[d.type]?.dash || null)
      .attr('stroke-opacity', d => d.type === 'org_member' ? 0.15 : 0.6)
      .attr('marker-end', d =>
        (d.type !== 'org_member' && d.type !== 'peer' && d.type !== 'market' && d.type !== 'branchlink')
          ? `url(#arrow-${d.type})`
          : null
      );

    // Draw nodes
    const node = g.append('g').attr('class', 'nodes')
      .selectAll('g')
      .data(visibleNodes)
      .join('g')
      .attr('cursor', 'pointer')
      .call(
        d3.drag()
          .on('start', (event, d) => {
            if (!event.active) simulation.alphaTarget(0.3).restart();
            d.fx = d.x; d.fy = d.y;
          })
          .on('drag', (event, d) => { d.fx = event.x; d.fy = event.y; })
          .on('end', (event, d) => {
            if (!event.active) simulation.alphaTarget(0);
            d.fx = null; d.fy = null;
          })
      )
      .on('click', (event, d) => {
        event.stopPropagation();
        setSelectedNode(d);
        if (onNodeClick) onNodeClick(d);
      })
      .on('mouseover', (event, d) => {
        setTooltip({
          x: event.pageX + 12,
          y: event.pageY - 8,
          node: d,
        });
      })
      .on('mouseout', () => setTooltip(null));

    // Org nodes: logos with data URI support
    const orgNodes = node.filter(d => d.nodeType === 'organization');

    // Helper function to create SVG logo data URI
    const createSVGLogo = (label, color) => {
      const svg = `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 100'>
        <circle cx='50' cy='50' r='45' fill='${color}' stroke='white' stroke-width='2'/>
        <text x='50' y='55' text-anchor='middle' font-size='24' font-weight='bold' fill='white'>${label}</text>
      </svg>`;
      return `data:image/svg+xml,${svg.replace(/\s+/g, ' ').replace(/#/g, '%23').replace(/'/g, '"')}`;
    };

    const colors = {
      'army': '#558b2f',
      'navy': '#03a9f4',
      'air_force': '#007eff',
      'space_force': '#40a9ff',
      'marine_corps': '#cc0000',
      'socom': '#a855f7',
      'osd': '#374151',
      'darpa': '#00d4ff',
      'mda': '#3b82f6',
      'dia': '#6366f1',
      'disa': '#d946ef',
    };

    // Add circles with service-branch colors (fallback for all orgs)
    orgNodes.append('circle')
      .attr('r', 24)
      .attr('fill', d => colors[(d.service_branch || '').toLowerCase()] || '#1E2D45')
      .attr('stroke', '#fff')
      .attr('stroke-width', 2)
      .attr('style', 'filter: drop-shadow(0 2px 4px rgba(0,0,0,0.3)); cursor: pointer;');

    // Add image elements for public logo URLs using foreignObject with CSS background
    orgNodes.filter(d => d.logo_url && !d.logo_url.includes('base64') && !d.logo_url.includes('data:'))
      .append('foreignObject')
      .attr('x', -20)
      .attr('y', -20)
      .attr('width', 40)
      .attr('height', 40)
      .style('border-radius', '50%')
      .style('overflow', 'hidden')
      .style('background-image', d => {
        const url = d.logo_url.startsWith('/')
          ? `${window.location.origin}${d.logo_url}`
          : d.logo_url;
        return `url('${url}')`;
      })
      .style('background-size', 'cover')
      .style('background-position', 'center')
      .style('background-repeat', 'no-repeat');

    // Add text labels only if no logo loaded
    orgNodes.filter(d => !d.logo_url || d.logo_url.includes('base64') || d.logo_url.includes('data:'))
      .append('text')
      .attr('dy', 6)
      .attr('text-anchor', 'middle')
      .attr('fill', '#fff')
      .attr('font-size', 14)
      .attr('font-weight', 'bold')
      .attr('pointer-events', 'none')
      .text(d => d.label?.substring(0, 2).toUpperCase() || '?');

    // Contact nodes: photos or circles
    const contactNodes = node.filter(d => d.nodeType === 'contact');

    // Photos (if available) using foreignObject with CSS background-image
    contactNodes.filter(d => d.photoUrl && !d.photoUrl.includes('base64') && !d.photoUrl.includes('data:'))
      .append('foreignObject')
      .attr('x', d => -nodeRadius(d))
      .attr('y', d => -nodeRadius(d))
      .attr('width', d => nodeRadius(d) * 2)
      .attr('height', d => nodeRadius(d) * 2)
      .style('border-radius', '50%')
      .style('overflow', 'hidden')
      .style('background-image', d => {
        const url = d.photoUrl.startsWith('/')
          ? `${window.location.origin}${d.photoUrl}`
          : d.photoUrl;
        return `url('${url}')`;
      })
      .style('background-size', 'cover')
      .style('background-position', 'center')
      .style('background-repeat', 'no-repeat');

    // Circles (no photo)
    contactNodes.filter(d => !d.photoUrl || d.photoUrl.includes('base64') || d.photoUrl.includes('data:'))
      .append('circle')
      .attr('r', d => nodeRadius(d))
      .attr('fill', d => {
        if (highlightIds.size > 0 && !highlightIds.has(d.id)) return '#1F2937';
        return nodeColor(d);
      })
      .attr('stroke', d => selectedNode?.id === d.id ? '#F9FAFB' : 'rgba(255,255,255,0.15)')
      .attr('stroke-width', d => selectedNode?.id === d.id ? 2.5 : 1);

    // Labels (below org logos, inside circles for contacts)
    node.filter(d => d.nodeType === 'contact').append('text')
      .attr('dy', d => nodeRadius(d) + 11)
      .attr('text-anchor', 'middle')
      .attr('fill', d => {
        if (highlightIds.size > 0 && !highlightIds.has(d.id)) return '#374151';
        return '#E5E7EB';
      })
      .attr('font-size', '9px')
      .attr('font-family', 'monospace')
      .attr('pointer-events', 'none')
      .text(d => d.label?.length > 18 ? d.label.slice(0, 16) + '…' : d.label);

    // Authority score ring on high-value contacts
    node.filter(d => d.nodeType === 'contact' && (d.authorityScore || 0) >= 75)
      .append('circle')
      .attr('r', d => nodeRadius(d) + 4)
      .attr('fill', 'none')
      .attr('stroke', '#22D3EE')
      .attr('stroke-width', 1.5)
      .attr('stroke-dasharray', '3,2')
      .attr('opacity', 0.6);

    // Tick
    let tickCount = 0;
    simulation.on('tick', () => {
      link
        .attr('x1', d => d.source.x)
        .attr('y1', d => d.source.y)
        .attr('x2', d => d.target.x)
        .attr('y2', d => d.target.y);
      node.attr('transform', d => `translate(${d.x},${d.y})`);

      // Auto-fit after a few ticks to let layout stabilize
      if (tickCount === 50) {
        setTimeout(() => fitToView(), 100);
      }
      tickCount++;
    });

    svg.on('click', () => setSelectedNode(null));

    return () => {
      simulation.stop();
      svg.selectAll('*').remove();
    };
  }, [graphData, activeRelTypes, showOrgMembers, selectedNode, highlightIds, loading, onNodeClick]);

  const toggleRelType = (type) => {
    setActiveRelTypes(prev => {
      const next = new Set(prev);
      next.has(type) ? next.delete(type) : next.add(type);
      return next;
    });
  };

  const fitToView = () => {
    if (!svgRef.current || !zoomRef.current) return;
    const svg = d3.select(svgRef.current);
    const g = svg.select('g');

    try {
      const bounds = g.node().getBBox();
      const fullWidth = svgRef.current.clientWidth || 900;
      const fullHeight = svgRef.current.clientHeight || 600;

      // Ensure we have valid bounds
      if (!bounds || bounds.width === 0 || bounds.height === 0) return;

      const midX = bounds.x + bounds.width / 2;
      const midY = bounds.y + bounds.height / 2;

      // Calculate scale with padding
      const padding = 0.9; // 10% padding around graph
      const scale = Math.min(fullWidth / bounds.width, fullHeight / bounds.height) * padding;

      // Center in viewport
      const tx = fullWidth / 2 - midX * scale;
      const ty = fullHeight / 2 - midY * scale;

      svg.transition().duration(500)
        .call(zoomRef.current.transform, d3.zoomIdentity.translate(tx, ty).scale(scale));
    } catch (e) {
      // Graph not ready yet
    }
  };

  const zoomIn = () => {
    if (!svgRef.current || !zoomRef.current) return;
    const svg = d3.select(svgRef.current);
    svg.transition().duration(300).call(zoomRef.current.scaleBy, 1.3);
  };

  const zoomOut = () => {
    if (!svgRef.current || !zoomRef.current) return;
    const svg = d3.select(svgRef.current);
    svg.transition().duration(300).call(zoomRef.current.scaleBy, 0.7);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: '600px', backgroundColor: '#0A0F1C' }}>
      {/* Controls bar */}
      <div className="controls-bar" style={{
        display: 'flex', flexWrap: 'wrap', gap: 'clamp(6px, 1.5vw, 8px)', alignItems: 'center',
        padding: 'clamp(8px, 2vw, 12px) clamp(10px, 3vw, 16px)', backgroundColor: '#111827', borderBottom: '1px solid #1E2D45',
        flexShrink: 0,
      }}>
        <input
          type="text"
          placeholder="Search…"
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          style={{
            background: '#111827', border: '1px solid #1E2D45', color: '#F1F5F9',
            padding: 'clamp(5px, 1.5vw, 8px) clamp(8px, 2vw, 10px)', borderRadius: '4px', fontSize: 'clamp(11px, 2.8vw, 12px)', flex: '1 1 150px', minWidth: '120px', minHeight: '36px',
          }}
        />

        <button
          onClick={() => setViewMode(v => (v === 'market' ? 'contacts' : 'market'))}
          title="Toggle the high-level market map: government offices (blue) ↔ companies (red) by shared PSC"
          style={{
            padding: '6px 12px', borderRadius: '4px', fontSize: '12px', fontWeight: 700, cursor: 'pointer', minHeight: '36px', flexShrink: 0,
            background: viewMode === 'market' ? '#DC2626' : '#111827',
            color: viewMode === 'market' ? '#fff' : '#94A3B8',
            border: `1px solid ${viewMode === 'market' ? '#DC2626' : '#1E2D45'}`,
          }}
        >
          {viewMode === 'market' ? '● Market map' : 'Market map'}
        </button>

        {viewMode === 'market' && (
          <>
            <select value={marketTier} onChange={e => setMarketTier(e.target.value)} title="PSC tier — Platform surfaces primes; Component surfaces suppliers; Service surfaces SETA/IT/R&D"
              style={{ background: '#111827', border: '1px solid #1E2D45', color: '#F1F5F9', padding: '6px 8px', borderRadius: '4px', fontSize: '12px', minHeight: '36px', flexShrink: 0 }}>
              <option value="">All PSC tiers</option>
              <option value="platform">Platforms (end items)</option>
              <option value="component">Components / subsystems</option>
              <option value="service">Services (R&D / IT / SETA)</option>
              <option value="other">Other / sustainment</option>
            </select>
            <select value={marketDept} onChange={e => setMarketDept(e.target.value)} title="Narrow to one department"
              style={{ background: '#111827', border: '1px solid #1E2D45', color: '#F1F5F9', padding: '6px 8px', borderRadius: '4px', fontSize: '12px', maxWidth: '190px', minHeight: '36px', flexShrink: 0 }}>
              <option value="">All departments</option>
              {marketDepts.map(d => <option key={d} value={d}>{d}</option>)}
            </select>
          </>
        )}

        <select
          value={orgFilter}
          onChange={e => setOrgFilter(e.target.value)}
          disabled={viewMode === 'market'}
          title="Focus the graph on one organization or company"
          style={{
            background: '#111827', border: '1px solid #1E2D45', color: '#F1F5F9',
            padding: '6px 8px', borderRadius: '4px', fontSize: '12px', maxWidth: '220px', minHeight: '36px', flexShrink: 0,
          }}
        >
          <option value="">All orgs (top 300 by authority)</option>
          {orgList
            .slice()
            .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
            .map(o => (
              <option key={o.id} value={o.id}>
                {(o.acronym ? `${o.acronym} — ` : '') + (o.name || '')} ({o.contact_count})
              </option>
            ))}
        </select>

        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
          <span style={{ color: '#94A3B8', fontSize: '11px', whiteSpace: 'nowrap' }}>Min:</span>
          <input
            type="range" min={0} max={90} step={10} value={minAuthority}
            onChange={e => setMinAuthority(parseInt(e.target.value))}
            style={{ width: '60px' }}
          />
          <span style={{ color: '#22D3EE', fontSize: '11px', width: '24px', textAlign: 'right' }}>{minAuthority}+</span>
        </div>

        <div style={{ width: '1px', height: '20px', background: '#1E2D45', display: 'none' }} />

        <label style={{ display: 'flex', alignItems: 'center', gap: '4px', color: '#6B7280', fontSize: '11px', cursor: 'pointer', whiteSpace: 'nowrap' }}>
          <input type="checkbox" checked={showOrgMembers} onChange={e => setShowOrgMembers(e.target.checked)} />
          Orgs
        </label>

        <div style={{ display: 'flex', gap: '4px', marginLeft: 'auto', flexShrink: 0 }}>
          <button
            onClick={zoomIn}
            title="Zoom in"
            style={{
              padding: '4px 10px', background: '#22D3EE44', color: '#22D3EE',
              border: '1px solid #22D3EE66', borderRadius: '4px', fontSize: '11px', cursor: 'pointer', fontWeight: 'bold',
            }}
          >
            +
          </button>
          <button
            onClick={zoomOut}
            title="Zoom out"
            style={{
              padding: '4px 10px', background: '#22D3EE44', color: '#22D3EE',
              border: '1px solid #22D3EE66', borderRadius: '4px', fontSize: '11px', cursor: 'pointer', fontWeight: 'bold',
            }}
          >
            −
          </button>
          <button
            onClick={fitToView}
            title="Fit to view"
            style={{
              padding: '4px 10px', background: '#22D3EE44', color: '#22D3EE',
              border: '1px solid #22D3EE66', borderRadius: '4px', fontSize: '11px', cursor: 'pointer', fontWeight: 'bold',
            }}
          >
            ⊡
          </button>
          <button
            onClick={loadGraph}
            title="Refresh"
            style={{
              padding: '4px 10px', background: '#22D3EE', color: '#0A0F1C',
              border: 'none', borderRadius: '4px', fontSize: '11px', cursor: 'pointer', fontWeight: 'bold',
            }}
          >
            ↻
          </button>
        </div>
      </div>

      {/* Connections filter */}
      <div style={{
        display: 'flex', flexWrap: 'wrap', gap: '4px', padding: '6px 16px',
        backgroundColor: '#0A0F1C', borderBottom: '1px solid #111827', fontSize: '10px',
      }}>
        <span style={{ color: '#94A3B8', fontSize: '10px', whiteSpace: 'nowrap' }}>Connections:</span>
        {ALL_REL_TYPES.map(type => (
          <button
            key={type}
            onClick={() => toggleRelType(type)}
            title={LINK_STYLES[type].label}
            style={{
              padding: '2px 6px', borderRadius: '3px', fontSize: '9px', cursor: 'pointer',
              border: `1px solid ${LINK_STYLES[type].stroke}`,
              background: activeRelTypes.has(type) ? LINK_STYLES[type].stroke + '33' : 'transparent',
              color: activeRelTypes.has(type) ? LINK_STYLES[type].stroke : '#6B7280',
              whiteSpace: 'nowrap', flex: '0 1 auto',
            }}
          >
            {LINK_STYLES[type].label}
          </button>
        ))}
      </div>

      {/* Legend */}
      <div style={{
        display: 'flex', gap: '8px', padding: '6px 16px',
        backgroundColor: '#0A0F1C', borderBottom: '1px solid #111827', flexWrap: 'wrap', fontSize: 'clamp(9px, 2vw, 10px)',
      }}>
        {Object.entries(NODE_COLORS).filter(([k]) => k !== 'organization').slice(0, 6).map(([type, color]) => (
          <div key={type} style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
            <div style={{ width: 8, height: 8, borderRadius: '50%', background: color, flexShrink: 0 }} />
            <span style={{ color: '#6B7280', fontSize: 'inherit', whiteSpace: 'nowrap' }}>
              {type.replace(/_/g, ' ')}
            </span>
          </div>
        ))}
        <div style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
          <div style={{ width: 10, height: 10, borderRadius: '50%', border: '1.5px dashed #22D3EE', background: 'transparent', flexShrink: 0 }} />
          <span style={{ color: '#6B7280', fontSize: 'inherit', whiteSpace: 'nowrap' }}>authority ≥75</span>
        </div>
      </div>

      {/* Graph canvas */}
      <div ref={containerRef} style={{ flex: 1, position: 'relative', overflow: 'hidden', backgroundColor: '#050B15', minHeight: '400px' }}>
        {loading && (
          <div style={{
            position: 'absolute', inset: 0, display: 'flex', alignItems: 'center',
            justifyContent: 'center', color: '#94A3B8', fontSize: '14px',
          }}>
            Loading network…
          </div>
        )}
        {error && (
          <div style={{
            position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'center', gap: '8px',
          }}>
            <span style={{ color: '#EF4444', fontSize: '14px' }}>{error}</span>
            <button onClick={loadGraph} style={{ padding: '6px 16px', background: '#22D3EE', color: '#0A0F1C', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
              Retry
            </button>
          </div>
        )}
        {!loading && !error && graphData.nodes.length === 0 && (
          <div style={{
            position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'center', gap: '12px',
          }}>
            <span style={{ color: '#94A3B8', fontSize: '16px' }}>No network data yet.</span>
            <span style={{ color: '#6B7280', fontSize: '13px' }}>Add contacts and relationships to build the graph.</span>
          </div>
        )}
        <svg ref={svgRef} style={{ width: '100%', height: '100%', display: 'block' }} />

        {/* Selected node detail panel */}
        {selectedNode && (
          <div style={{
            position: 'absolute', top: 12, right: 12, width: 'clamp(220px, 90vw, 300px)', maxWidth: 'calc(100% - 24px)',
            background: '#111827', border: '1px solid #1E2D45', borderRadius: '8px',
            padding: '14px', color: '#F1F5F9', fontSize: '12px', boxSizing: 'border-box',
          }}>
            <button
              onClick={() => setSelectedNode(null)}
              style={{ position: 'absolute', top: 8, right: 8, background: 'none', border: 'none', color: '#6B7280', cursor: 'pointer', fontSize: '16px' }}
            >
              ×
            </button>
            {selectedNode.nodeType === 'organization' ? (
              (selectedNode.market === 'office' || selectedNode.market === 'company') ? (() => {
                const nodeById = new Map(graphData.nodes.map(n => [n.id, n]));
                const idOf = e => (typeof e === 'object' ? e.id : e);
                const conns = graphData.links.filter(l => l.type === 'market' && (idOf(l.source) === selectedNode.id || idOf(l.target) === selectedNode.id));
                const other = l => nodeById.get(idOf(l.source) === selectedNode.id ? idOf(l.target) : idOf(l.source));
                const isOffice = selectedNode.market === 'office';
                return (
                  <>
                    <div style={{ fontWeight: 'bold', fontSize: '14px', color: isOffice ? '#60A5FA' : '#F87171', marginBottom: '4px' }}>{selectedNode.fullName || selectedNode.label}</div>
                    <div style={{ color: '#94A3B8', marginBottom: '6px', fontSize: '11px' }}>{isOffice ? 'Government office' : 'Company'} · {selectedNode.contactCount} PSCs · {conns.length} {isOffice ? 'companies' : 'offices'}</div>
                    <div style={{ maxHeight: '320px', overflowY: 'auto' }}>
                      {conns.map((l, i) => { const o = other(l); const codes = (l.shared || []).map(c => marketMeta[c]?.title ? `${c} ${marketMeta[c].title}` : c); return (
                        <div key={i} style={{ borderBottom: '1px solid #1E2D45', padding: '4px 0' }}>
                          <div style={{ color: '#E5E7EB', fontSize: '12px', fontWeight: 600 }}>{o?.label || ''}</div>
                          <div style={{ color: '#6B7280', fontSize: '10px' }}>{codes.slice(0, 8).join(' · ')}</div>
                        </div>); })}
                    </div>
                  </>
                );
              })() : (
                <>
                  <div style={{ fontWeight: 'bold', fontSize: '14px', color: '#22D3EE', marginBottom: '6px' }}>
                    {selectedNode.fullName || selectedNode.label}
                  </div>
                  <div style={{ color: '#94A3B8' }}>Type: {selectedNode.orgType?.replace(/_/g, ' ')}</div>
                  <div style={{ color: '#94A3B8' }}>Contacts: {selectedNode.contactCount}</div>
                </>
              )
            ) : (
              <>
                <div style={{ fontWeight: 'bold', fontSize: '14px', color: '#22D3EE', marginBottom: '4px' }}>
                  {selectedNode.fullName}
                </div>
                <div style={{ color: '#94A3B8', marginBottom: '2px' }}>{selectedNode.title}</div>
                <div style={{ color: '#6B7280', marginBottom: '6px' }}>{selectedNode.orgName}</div>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '6px' }}>
                  <span style={{
                    background: nodeColor(selectedNode) + '33',
                    color: nodeColor(selectedNode),
                    padding: '2px 8px', borderRadius: '3px', fontSize: '10px',
                  }}>
                    {selectedNode.positionType?.replace(/_/g, ' ')}
                  </span>
                  {(selectedNode.rankCode || selectedNode.gradeCode) && (
                    <span style={{ background: '#111827', color: '#22D3EE', padding: '2px 8px', borderRadius: '3px', fontSize: '10px' }}>
                      {selectedNode.rankCode || selectedNode.gradeCode}
                    </span>
                  )}
                </div>
                <div style={{ display: 'flex', gap: '12px', marginBottom: '6px' }}>
                  <div>
                    <div style={{ color: '#6B7280', fontSize: '10px' }}>AUTHORITY</div>
                    <div style={{ color: '#22D3EE', fontWeight: 'bold' }}>{selectedNode.authorityScore || 'N/A'}</div>
                  </div>
                  <div>
                    <div style={{ color: '#6B7280', fontSize: '10px' }}>RELATIONSHIP</div>
                    <div style={{ color: '#F1F5F9' }}>{'★'.repeat(selectedNode.relationshipStrength || 0)}</div>
                  </div>
                  <div>
                    <div style={{ color: '#6B7280', fontSize: '10px' }}>STAGE</div>
                    <div style={{ color: '#F1F5F9' }}>{selectedNode.relationshipStage}</div>
                  </div>
                </div>
                {selectedNode.functionAreas?.length > 0 && (
                  <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                    {selectedNode.functionAreas.map(fa => (
                      <span key={fa} style={{ background: '#111827', color: '#94A3B8', padding: '1px 5px', borderRadius: '3px', fontSize: '9px' }}>
                        {fa}
                      </span>
                    ))}
                  </div>
                )}
                {selectedNode.lastInteraction && (
                  <div style={{ color: '#6B7280', fontSize: '10px', marginTop: '6px' }}>
                    Last contact: {new Date(selectedNode.lastInteraction).toLocaleDateString()}
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>

      {/* D3 tooltip */}
      {tooltip && (
        <div style={{
          position: 'fixed', left: tooltip.x, top: tooltip.y,
          background: '#111827', border: '1px solid #1E2D45', borderRadius: '6px',
          padding: '8px 12px', color: '#F1F5F9', fontSize: '11px',
          pointerEvents: 'none', zIndex: 9999, maxWidth: '200px',
        }}>
          <strong>{tooltip.node.fullName || tooltip.node.label}</strong>
          {tooltip.node.nodeType === 'contact' && (
            <>
              {tooltip.node.title && <div style={{ color: '#94A3B8' }}>{tooltip.node.title}</div>}
              {tooltip.node.orgName && <div style={{ color: '#6B7280' }}>{tooltip.node.orgName}</div>}
              <div style={{ color: '#22D3EE', marginTop: '2px' }}>
                Authority: {tooltip.node.authorityScore || 'N/A'}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
